import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, FormControlLabel, Checkbox, Chip, FormHelperText, TextField } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { DatePicker } from '../../../Paylinks/components';
import { useGetPublicMerchantQuery } from '../../../../store/modules/merchant/api';
import { CustomerAutoComplete, getOptionLabel } from '../../../../common/CustomerAutoComplete';
import {
  BaseFilterSection as Section,
  filterSelectAllRowSx,
} from '../../../Paylinks/components/BaseFiltersPanel';
import { MITStatus } from '../../../../store/modules/mit/types';
import { MIT_FILTER_STATUSES, MITFiltersFormValues } from '../../../../hooks/useMITFiltersForm';

const getMITStatusLabel = (status: MITStatus): string => {
  switch (status) {
    case MITStatus.PAID:
      return 'Paid';
    case MITStatus.FAILED:
      return 'Failed';
    case MITStatus.PARTIALLY_REFUNDED:
      return 'Partially Refunded';
    case MITStatus.REFUNDED:
      return 'Refunded';
    default:
      return status;
  }
};

interface Props {
  merchantId: string;
  requiredFields?: (keyof MITFiltersFormValues)[];
  hookFormProps: {
    watch: UseFormWatch<MITFiltersFormValues>;
    setValue: UseFormSetValue<MITFiltersFormValues>;
    control: Control<MITFiltersFormValues>;
    errors: FieldErrors<MITFiltersFormValues>;
  };
}

export const MITFilters = ({ merchantId, hookFormProps, requiredFields }: Props) => {
  const { watch, setValue, control, errors } = hookFormProps;

  const { data: publicData } = useGetPublicMerchantQuery(merchantId, { skip: !merchantId });
  const acceptedCurrencySymbol = publicData?.acceptedCurrency?.symbol || '$';

  const selectedStatuses = watch('status');
  const selectedCustomers = watch('customers');
  const createdAfter = watch('createdAfter');
  const createdBefore = watch('createdBefore');

  return (
    <>
      <Section
        title="Status"
        required={requiredFields?.includes('status')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '& > *': {
            height: '24px',
            width: 'fit-content',
          },
          '& span': {
            textTransform: 'capitalize',
            fontSize: 14,
          },
        }}
      >
        <FormControlLabel
          sx={filterSelectAllRowSx}
          control={
            <Checkbox
              indeterminate={
                selectedStatuses.length > 0 && selectedStatuses.length < MIT_FILTER_STATUSES.length
              }
              checked={selectedStatuses.length === MIT_FILTER_STATUSES.length}
              onChange={(e) =>
                e.target.checked
                  ? setValue('status', MIT_FILTER_STATUSES as string[])
                  : setValue('status', [])
              }
            />
          }
          label="Select All"
        />
        {MIT_FILTER_STATUSES.map((status) => (
          <FormControlLabel
            key={status}
            control={
              <Checkbox
                checked={selectedStatuses.includes(status)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('status', [...selectedStatuses, status]);
                  } else {
                    setValue(
                      'status',
                      selectedStatuses.filter((s) => s !== status),
                    );
                  }
                }}
              />
            }
            label={getMITStatusLabel(status)}
          />
        ))}
        {errors?.status && (
          <FormHelperText error sx={{ fontSize: 10, mt: 0.5 }}>
            {errors.status.message}
          </FormHelperText>
        )}
      </Section>

      <Section
        title="Date Created"
        required={
          requiredFields?.includes('createdAfter') || requiredFields?.includes('createdBefore')
        }
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['createdAfter', 'createdBefore'] as const).map((name) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <DatePicker
                  key={name}
                  label={name === 'createdAfter' ? 'Start date' : 'End date'}
                  errorMessage={errors[name]?.message}
                  disablePast={false}
                  dateOnly
                  shouldDisableDate={(date) => {
                    if (name === 'createdAfter' && createdBefore) {
                      return date.isAfter(createdBefore);
                    }

                    if (name === 'createdBefore' && createdAfter) {
                      return date.isBefore(createdAfter);
                    }

                    return false;
                  }}
                  closeOnSelect
                  {...field}
                />
              )}
            />
          ))}
        </Box>
      </Section>

      <Section title="Amount">
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['amountGTE', 'amountLTE'] as const).map((name) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <NumericFormat
                  key={name}
                  customInput={TextField}
                  variant="outlined"
                  label={name === 'amountGTE' ? 'Minimum' : 'Maximum'}
                  thousandSeparator
                  prefix={acceptedCurrencySymbol}
                  fixedDecimalScale
                  decimalScale={2}
                  allowNegative={false}
                  error={!!errors?.[name]}
                  helperText={errors?.[name]?.message}
                  onChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  id={field.name}
                />
              )}
            />
          ))}
        </Box>
      </Section>

      <Section title="Customer (s)">
        <CustomerAutoComplete
          multiple
          extraQueryParams={{ cardOnFile: true }}
          inputProps={{
            label: 'Search by name, phone or email',
          }}
          value={selectedCustomers}
          onChange={(e, value) => {
            const selected = selectedCustomers;
            const selectedIds = selected.map((c) => c.payLinkCustomerId);
            if (value) {
              const newCustomers = value.filter((c) => !selectedIds.includes(c.payLinkCustomerId));
              setValue('customers', [...selected, ...newCustomers]);
            }
          }}
          renderTags={() => null}
        />
        {selectedCustomers.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {selectedCustomers.map((customer) => (
              <Chip
                key={customer.payLinkCustomerId}
                label={getOptionLabel(customer)}
                onDelete={() => {
                  setValue(
                    'customers',
                    selectedCustomers.filter(
                      (c: any) => c.payLinkCustomerId !== customer.payLinkCustomerId,
                    ),
                  );
                }}
                deleteIcon={<Close sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: 12,

                  '& .MuiChip-deleteIcon': {
                    fontSize: 16,
                  },
                }}
              />
            ))}
          </Box>
        )}
        {errors?.customers && (
          <FormHelperText error sx={{ fontSize: 10, mt: 0.5 }}>
            {errors.customers.message}
          </FormHelperText>
        )}
      </Section>
    </>
  );
};
